// Melatonin 3 mg is within licence for jet lag. No weight banding.
// Single brand. Dose may be increased to 6 mg if 3 mg insufficient —
// modelled as a completionNotes note, not a separate schedule or brand.
// requiresCHISNotificationUnder19: false. Maximum 20 tablets per supply.

import { registerTier2Config } from "../tier2ConfigRegistry";
import { PGD_IDS } from "../../pgds/pgdIds";

registerTier2Config({
  pgdId: PGD_IDS.MELATONIN,
  displayName: "Melatonin 3 mg (jet lag)",
  serviceType: "supply",
  minimumAgeYears: 18,
  indicationSummary:
    "Short-term treatment of jet lag in adults aged 18 and over travelling on long-haul flights crossing 5 or more time zones.",

  brands: [
    {
      brandId: "melatonin-3mg",
      brandName: "Melatonin 3 mg",
      minimumAgeYears: 18,
      route: "supply",
      doseDescription:
        "3 mg (one tablet) taken 30 minutes before bed on the day of arrival at destination; continue once daily for up to 5 days",
      administrationNotes:
        "Supply melatonin 3 mg tablets. Label: take one tablet 30 minutes before bed on the day of arrival. Do not take before 20:00 or after 04:00 destination time. Continue for up to 5 days. If 3 mg is insufficient, dose may be increased to 6 mg (two tablets) \u2014 use the lowest effective dose for the shortest duration. Maximum 20 tablets per supply. Up to 16 courses may be supplied within any 12-month period.",
    },
  ],

  schedules: [
    {
      scheduleId: "melatonin-jet-lag",
      label: "Once daily at bedtime \u2014 start on arrival at destination; up to 5 days",
      offLabel: false,
      doses: [
        {
          doseNumber: 1,
          label: "First dose: 30 minutes before bed on the day of arrival at destination",
        },
      ],
      completionNotes:
        "Continue once daily for up to 5 days. Do not take before 20:00 or after 04:00 destination time. If 3 mg (one tablet) is insufficient, the dose may be increased to 6 mg (two tablets) \u2014 use the lowest effective dose for the shortest duration. For the return flight: if required, take the first dose 30 minutes before bed on arrival back. Maximum 20 tablets per supply. Maximum 16 courses in any 12-month period.",
    },
  ],

  capturesPriorDoseHistory: false,

  supplyGates: [
    "Patient is aged 18 or over.",
    "Patient is travelling on a long-haul flight crossing 5 or more time zones.",
  ],

  exclusions: [
    {
      checkId: "melatonin-ex-age",
      question: "Is the patient under 18 years of age?",
      disqualifyingAnswer: "yes",
      disqualificationMessage: "Melatonin supply under this PGD is restricted to patients aged 18 and over. Do not supply.",
      referralMessage: "Refer to the patient's GP.",
    },
    {
      checkId: "melatonin-ex-hypersensitivity",
      question: "Does the patient have known hypersensitivity to melatonin or any excipient of the selected product?",
      disqualifyingAnswer: "yes",
      disqualificationMessage: "Melatonin is contraindicated in this patient. Do not supply.",
      referralMessage: "Refer to the patient's GP.",
    },
    {
      checkId: "melatonin-ex-pregnancy-breastfeeding",
      question: "Is the patient known or suspected to be pregnant, or breastfeeding?",
      disqualifyingAnswer: "yes",
      disqualificationMessage: "Melatonin is contraindicated in pregnancy and breastfeeding. Do not supply.",
      referralMessage: "Refer to the patient's GP.",
    },
    {
      checkId: "melatonin-ex-fluvoxamine",
      question: "Is the patient taking fluvoxamine?",
      disqualifyingAnswer: "yes",
      disqualificationMessage: "Fluvoxamine is contraindicated with melatonin. Do not supply.",
      referralMessage: "Refer to the patient's GP for advice on jet lag management.",
    },
  ],

  cautions: [
    {
      checkId: "melatonin-caut-benzodiazepines",
      question: "Is the patient taking benzodiazepines or Z-drugs (e.g. zopiclone, zolpidem, zaleplon)?",
      cautionMessage:
        "Melatonin may enhance the sedative effects of benzodiazepines and Z-drugs. Advise the patient to use caution and avoid driving or operating machinery if affected.",
    },
    {
      checkId: "melatonin-caut-anticoagulants",
      question: "Is the patient taking warfarin or a direct oral anticoagulant (DOAC)?",
      cautionMessage:
        "Melatonin may enhance the anticoagulant effect of warfarin and DOACs. Advise the patient to seek advice from their GP before supply and to monitor for signs of bleeding.",
    },
    {
      checkId: "melatonin-caut-cyp1a2",
      question: "Is the patient taking a CYP1A2 inhibitor such as a quinolone antibiotic (e.g. ciprofloxacin) or cimetidine?",
      cautionMessage: "CYP1A2 inhibitors may raise melatonin plasma levels and increase side effects such as drowsiness. Advise the patient accordingly.",
    },
    {
      checkId: "melatonin-caut-renal-hepatic",
      question: "Does the patient have severe renal impairment or moderate to severe hepatic impairment?",
      cautionMessage:
        "Melatonin should be avoided in patients with severe renal impairment or moderate to severe hepatic impairment. Consider referral to the patient's GP before supply.",
    },
    {
      checkId: "melatonin-caut-cardiovascular",
      question: "Does the patient have a cardiovascular condition for which they are taking antihypertensive medicines?",
      cautionMessage: "Melatonin is not recommended in patients with cardiovascular conditions taking antihypertensive medicines. Consider referral to the patient's GP before supply.",
    },
    {
      checkId: "melatonin-caut-lactose",
      question: "Has the patient been diagnosed with lactose intolerance or rare hereditary galactose intolerance?",
      cautionMessage:
        "Some melatonin products contain lactose. Check the specific product labelling \u2014 if the selected product contains lactose and the patient has lactose intolerance or hereditary galactose intolerance, supply a lactose-free formulation (e.g. Pharma Nord, Syncrodin\u00ae, or Ceyesto\u00ae).",
    },
    {
      checkId: "melatonin-caut-drowsiness-driving",
      question: "Has the pharmacist counselled the patient on drowsiness and the timing restriction?",
      cautionMessage:
        "Melatonin may cause drowsiness. Advise the patient not to drive or operate machinery if affected. Do not take before 20:00 or after 04:00 destination time. Avoid alcohol while taking melatonin. Avoid food for 2 hours before and after taking the dose.",
    },
  ],

  requiresICVP: false,
  requiresLariamCard: false,
  requiresYellowCardDiscussion: false,
  requiresCHISNotificationUnder19: false,
  requiresGPNotification: true,
  standardObservationMinutes: 0,
  extendedObservationMinutes: 0,

  patientAdvicePoints: [
    "Take one 3 mg tablet 30 minutes before bed on the day you arrive at your destination.",
    "Do not take before 20:00 or after 04:00 destination time.",
    "Continue once daily for up to 5 days.",
    "If 3 mg does not help sufficiently, you may take 6 mg (two tablets) instead \u2014 use the lowest dose that works for you for the shortest time needed.",
    "Melatonin may make you drowsy. Do not drive or operate machinery if you feel affected.",
    "Avoid alcohol while taking melatonin.",
    "Avoid food and drink for 2 hours before and after taking each dose.",
    "For the return journey: if needed, take the first dose 30 minutes before bed on the evening you arrive back home.",
  ],
});
